import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { CurrencyValue } from '../../../common/currencyValue';
import { constants } from '../../../common/constants/constants';
import BllinkUtils from '../../../common/utils/utils';
import Button from '../../../common/Button';
import { graySelectStyles } from '../../../common/shared/select.styled';
import { showLoader, hideLoader } from '../../../store/common/slice';
import Whatsapp from '../../../images/newDesign/whatsapp_icon.svg';
import Email from '../../../images/newDesign/email_icon.svg';
import IVR from '../../../images/newDesign/ivr_icon.svg';
import arrow from '../../../images/newDesign/black_arrow.svg';
import { StyledFinancialData } from './FinancialData.styled';
import CircleProgressBar from './CircleProgressBar';
import { PieChart, LineGraph } from './Charts';

const Money = BllinkUtils.Money;

const FinancialData = ({ buildingID }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const ltr = useSelector((state) => state.common.ltrEnabled);

    const [currency, setCurrency] = useState(constants.defaultCurrency);
    const [expenses, setExpenses] = useState();
    const [incomes, setIncomes] = useState();
    const [requests, setRequests] = useState();
    const [pieChartData, setPieChartData] = useState([]);
    const [lineGraphExpenses, setLineGraphExpenses] = useState([]);
    const [lineGraphIncomes, setLineGraphIncomes] = useState([]);
    const [showExpenses, setShowExpenses] = useState(true);
    const [showIncomes, setShowIncomes] = useState(false);

    const monthYearOptions = [
        {
            value: 'month',
            label: t('payment_settings.month')
        },
        {
            value: 'year',
            label: t('payment_settings.year')
        }
    ];
    const [currentMonthYear, setCurrentMonthYear] = useState(monthYearOptions[0]);

    const handleMonthYearChanged = e => setCurrentMonthYear(e);

    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    useEffect(async () => {
        if (!client) return;

        dispatch(showLoader());

        const response = await client.managers.dashboard.getBalanceTotal(buildingID, year, month);
        if (response) {
            setExpenses({
                percentage: Money.roundNumber(response.expenseTotal.value*100/response.expenseExpectedTotal.value),
                total: response.expenseTotal.value
            });
            setIncomes({
                percentage: Money.roundNumber(response.incomeTotal.value*100/response.incomeExpectedTotal.value),
                total: response.incomeTotal.value
            });
            setCurrency(response.expenseTotal.currency);
            const mappedPaymentMethods = response.incomeByPaymentTypeTotals.map(({ paymentMethod, amount }) => {
                const mapped = {
                    name: constants.APIContract.paymentMethodsById[paymentMethod].serverKey,
                    totalPaid: Money.roundNumber(amount.value / 100, 2)
                };
                return mapped
            })
            setPieChartData(mappedPaymentMethods);
        }
        const { paymentRequests } = await client.managers.dashboard.getPaymentRequestStats(buildingID, year, month);
        if (paymentRequests) {
            let request = {};
            paymentRequests.map(({ transport, count}) => {
                request = {...request, [transport]: count}
                return request
            })
            setRequests(request)
        } 

        const { paymentTotalsHistory: expenseHistory } = await client.managers.dashboard.getExpenseTotalsHistory(buildingID);
        if (expenseHistory) setLineGraphExpenses(expenseHistory);

        const { paymentTotalsHistory: incomeHistory } = await client.managers.dashboard.getPaymentTotalsHistory(buildingID);
        if (incomeHistory) setLineGraphIncomes(incomeHistory)
        
        dispatch(hideLoader());
    }, [client]);

    const onShowExpensesClick = () => {
        if (showExpenses) setShowIncomes(true);
        setShowExpenses(!showExpenses);
    };
    const onShowIncomesClick = () => {
        if (showIncomes) setShowExpenses(true)
        setShowIncomes(!showIncomes)
    };

    return (
        <StyledFinancialData income={incomes?.percentage} expense={expenses?.percentage}>
            <div className='general-data-title'>
                {t('buildings.financial_data')}

                <Select
                    className='select'
                    styles={graySelectStyles}
                    options={monthYearOptions}
                    value={currentMonthYear}
                    onChange={handleMonthYearChanged}
                    isRtl={!ltr}
                />
            </div>

            <div className='financial-data-wrapper'>
                <div className='payment-request'>
                    <div className='financial-data-title'>{t('reports.paymentRequestPopUp.paymentTitle')}</div>
                    <div className='payments'>
                        <div className='payment-data'>
                            <img src={Whatsapp} />
                            <span className='messenger'>{t('buildings.whatsapp')}</span>
                            <span className='number'>{requests?.whatsapp}</span>
                        </div>

                        <div className='payment-data'>
                            <img src={Email} />
                            <span className='messenger'>{t('expenses.email')}</span>
                            <span className='number'>{requests?.email}</span>
                        </div>

                        <div className='payment-data'>
                            <img src={IVR} />
                            <span className='messenger'>{t('buildings.ivr')}</span>
                            <span className='number'>{requests?.ivr}</span>
                        </div>
                    </div>
                </div>

                <div className='pie-chart'>
                    <div className='financial-data-title'>{t('buildings.payment_method_type')}</div>
                    <div className='canvas-pie'>
                        <PieChart pieChartData={pieChartData} />
                    </div>
                </div>

                <div className='incomes-expenses'>
                    <div className='round-progressbar-container'>
                        <CircleProgressBar percentage={incomes?.percentage} blue />
                        <img className='arrow transform' src={arrow} />
                    </div>
                    <div className='financial-data-title'>{t('buildings.incomes')}</div>
                    <div className='income-expense-data'><CurrencyValue currency={currency}>{incomes?.total}</CurrencyValue></div>
                    <div className='income-expense-data'>{incomes?.percentage}%</div>
                    <div className='progress-bar'><div className='percentage incomes'></div></div>
                </div>

                <div className='line-graph'>
                    <div className='buttons'>
                        <Button 
                            className={showExpenses ? '' : 'not-active'}
                            onClick={onShowExpensesClick}
                        >
                            {t('buildings.expenses')}
                        </Button>
                        <Button 
                            className={showIncomes ? '' : 'not-active'}
                            onClick={onShowIncomesClick}
                        >
                            {t('buildings.incomes')}
                        </Button>
                    </div>

                    <LineGraph 
                        expenses={lineGraphExpenses}
                        incomes={lineGraphIncomes}
                        showExpenses={showExpenses}
                        showIncomes={showIncomes}
                        ltr={ltr}
                    />
                </div>

                <div className='incomes-expenses'>
                    <div className='round-progressbar-container'>
                        <CircleProgressBar percentage={expenses?.percentage} />
                        <img className='arrow' src={arrow} />
                    </div>
                    <div className='financial-data-title'>{t('buildings.expenses')}</div>
                    <div className='income-expense-data'><CurrencyValue currency={currency}>{expenses?.total}</CurrencyValue></div>
                    <div className='income-expense-data'>{expenses?.percentage}%</div>
                    <div className='progress-bar'><div className='percentage expenses'></div></div>
                </div>
            </div>
        </StyledFinancialData>
    )
}

export default FinancialData;
